/**
 * Hostname → org lookup (NOS-242 / NOS-247).
 *
 * Shared by the Astro public app, the SPA, and any Node SSR surface.
 * Deno edge functions consume it through src/lib/seo/siteOrigin.ts, which
 * re-exports from this file via a relative `.ts` import (Deno cannot
 * resolve the bare workspace specifier). Keep this file zero-import.
 */

export const ROOT_DOMAIN = "getnos.ai"

/**
 * Canonical subdomain rules (NOS-257). ONE reserved list, ONE regex, ONE
 * length rule — signup, org-settings rename, admin rename, the SPA router,
 * the edge middleware, the Astro app, and Deno functions all consume these.
 * The SPA imports them through src/lib/subdomainRules.ts, which adds the
 * supabase-backed availability check.
 */

export const SUBDOMAIN_MIN_LENGTH = 6
export const SUBDOMAIN_MAX_LENGTH = 30

/** Lowercase alphanumeric + hyphens; no leading/trailing/consecutive hyphens. */
export const SUBDOMAIN_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/** Hostnames on ROOT_DOMAIN that are platform surfaces, never org sites. */
export const RESERVED_SUBDOMAINS = new Set<string>([
  // Union of the three pre-NOS-257 validator lists
  "www",
  "api",
  "admin",
  "app",
  "dashboard",
  "login",
  "signup",
  "mail",
  "ftp",
  "webmail",
  "smtp",
  "pop",
  "ns1",
  "ns2",
  "staging",
  // Infra set (Justin, 2026-07-31)
  "cdn",
  "assets",
  "status",
  "email",
  "dev",
  "test",
  "demo",
])

/**
 * Org slugs that predate the NOS-257 rules and violate them (too short or
 * reserved-name collisions). They keep resolving and stay valid; only new
 * registrations and renames are held to the rules. Seeded from the prod +
 * staging slug audit — staging audited clean 2026-07-31; prod audited
 * 2026-08-01 and is clean (all pre-launch test orgs were deleted rather
 * than grandfathered). Empty until a real org ever needs an exemption.
 */
export const GRANDFATHERED_SLUGS = new Set<string>([])

/**
 * True when a *.getnos.ai subdomain is a platform surface the router must
 * never resolve to an org site. Grandfathered org slugs are exempt.
 */
export function isPlatformSubdomain(subdomain: string): boolean {
  return RESERVED_SUBDOMAINS.has(subdomain) && !GRANDFATHERED_SLUGS.has(subdomain)
}

export interface SubdomainValidation {
  valid: boolean
  error?: string
}

/**
 * Validate a subdomain/slug for registration or rename. Grandfathered slugs
 * pass unconditionally (DB uniqueness decides who may hold them); everything
 * else gets the 6–30 length rule, the format regex, and the reserved list.
 */
export function validateSubdomain(subdomain: string): SubdomainValidation {
  const normalized = subdomain.trim().toLowerCase()
  if (normalized.length === 0) {
    return { valid: false, error: "Subdomain is required" }
  }
  if (GRANDFATHERED_SLUGS.has(normalized)) {
    return { valid: true }
  }
  if (normalized.length < SUBDOMAIN_MIN_LENGTH) {
    return { valid: false, error: `Must be at least ${SUBDOMAIN_MIN_LENGTH} characters` }
  }
  if (normalized.length > SUBDOMAIN_MAX_LENGTH) {
    return { valid: false, error: `Must be at most ${SUBDOMAIN_MAX_LENGTH} characters` }
  }
  if (!SUBDOMAIN_REGEX.test(normalized)) {
    return {
      valid: false,
      error:
        "Only lowercase letters, numbers, and hyphens. Cannot start or end with a hyphen or contain consecutive hyphens.",
    }
  }
  if (RESERVED_SUBDOMAINS.has(normalized)) {
    return { valid: false, error: "This subdomain is reserved" }
  }
  return { valid: true }
}

export type HostResolution =
  | { type: "slug"; slug: string }
  | { type: "custom_domain"; domain: string }
  | null

/**
 * Map a request hostname to an org lookup.
 * - <slug>.getnos.ai → slug lookup (reserved platform subdomains rejected)
 * - <slug>.localhost → slug lookup (local Astro/SPA soak)
 * - anything else with a dot → custom-domain lookup
 * - apex getnos.ai, localhost, empty → null (not an org site)
 */
export function resolveHost(rawHost: string | null | undefined): HostResolution {
  if (!rawHost) return null
  const host = rawHost.trim().toLowerCase().replace(/:\d+$/, "")
  if (!host || host === "localhost" || host === ROOT_DOMAIN || host === `www.${ROOT_DOMAIN}`) {
    return null
  }
  if (host.endsWith(`.${ROOT_DOMAIN}`)) {
    const sub = host.slice(0, -(ROOT_DOMAIN.length + 1))
    if (!sub || sub.includes(".") || isPlatformSubdomain(sub)) {
      return null
    }
    return { type: "slug", slug: sub }
  }
  // Local soak: hopeforall.localhost → slug
  if (host.endsWith(".localhost")) {
    const sub = host.slice(0, -".localhost".length)
    if (!sub || sub.includes(".")) return null
    return { type: "slug", slug: sub }
  }
  if (!host.includes(".")) return null
  return { type: "custom_domain", domain: host }
}

/**
 * Map URL pathname to page slug(s) to try.
 * Handles "home" and "/about" (seed) vs "about" (UI).
 */
export function pathToSlugs(pathname: string): string[] {
  const normalized = pathname.replace(/^\//, "").toLowerCase() || "home"
  if (normalized === "home") return ["home"]
  return [normalized, `/${normalized}`]
}

/** www ↔ apex candidates for custom_domains lookup. */
export function customDomainCandidates(hostname: string): string[] {
  const host = hostname.trim().toLowerCase().replace(/:\d+$/, "")
  const candidates = [host]
  if (host.startsWith("www.")) candidates.push(host.slice(4))
  else candidates.push(`www.${host}`)
  return candidates
}
